import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Tooltip,
  Badge,
  Divider,
  ListItem,
  List, ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as ExportIcon,
  Upload as ImportIcon,
  Warning as WarningIcon,
  CheckCircle as ValidIcon,
  Error as ExpiredIcon,
  Schedule as ExpiringIcon,
  Description as DocumentIcon,
  Person as PersonIcon,
  DirectionsCar as VehicleIcon,
  Business as CompanyIcon,
  Assignment as ContractIcon,
  LocalShipping as VoyageIcon,
  Receipt as InvoiceIcon,
  ContactPhone as ContactIcon,
  AccountCircle as UserIcon,
  AttachMoney as TarificationIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { API } from '../../API/api';
import { useAuth } from '../../Context/AuthContext';
import AutocompleteComboBox from '../../Components/AutocompleteComboBox';
import TextInputMui from '../../Components/TextInputMui';
import ButtonAttribute from '../../Components/ButtonAttribute';
import DateTimePickerComponent from '../../Components/DateTimePicker';
import { logOperation } from "../../API/logOperation"; // adjust path
import dayjs from 'dayjs';

const DocumentManagement = () => {
  const { authState } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openMultipleDialog, setOpenMultipleDialog] = useState(false); // Separate state for multiple documents dialog
  const [editingDocument, setEditingDocument] = useState(null);

  const [formData, setFormData] = useState({
    document_type: '',
    document_name: '',
    issue_date: dayjs().format('YYYY-MM-DD'),   // today
    expiry_date: dayjs().format('YYYY-MM-DD'),  // today
    related_entity_type: '',
    related_entity_id: '',
    comments: '',
  });

  const [multipleDocuments, setMultipleDocuments] = useState([]);
  const [entityOptions, setEntityOptions] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [formOptions, setFormOptions] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const parsedAuthState = JSON.parse(sessionStorage.getItem('authLocalstorage'));

  // KPI States
  const [kpis, setKpis] = useState({
    total: 0,
    valid: 0,
    expiring: 0,
    expired: 0,
  });

  // Document types and entity types
  const documentTypes = {
    assurance: 'Assurance',
    carte_grise: 'Carte grise',
    visite_technique: 'Visite technique',
    agrement: 'Agrément',
    vignette_vehicule: 'Vignette',
    tachygraphe: 'Tachygraphe',
    extincteur_1: 'Extincteur 1',
    extincteur_2: 'Extincteur 2',
    vignette: 'Vignette',
    license: 'Permis',
    medical_certificate: 'Certificat médical',
    registration: 'Immatriculation',
    inspection: 'Contrôle technique',
    permit: 'Autorisation',
    certificate: 'Certificat',
    contract: 'Contrat',
    agreement: 'Accord',
    invoice: 'Facture',
    receipt: 'Reçu',
    warranty: 'Garantie',
    maintenance_record: 'Carnet d\'entretien',
    tax_document: 'Document fiscal',
    customs_document: 'Document douanier',
  };

  const entityTypes = {
    driver: 'Chauffeur',
    vehicle: 'Véhicule',
    company: 'Société',
    voyage: 'Voyage',
    invoice: 'Facture',
    interlocuteur: 'Contact',
    user: 'Utilisateur',
    tarification: 'Tarification',
  };

  const statusColors = {
    valid: 'success',
    expiring_soon: 'warning',
    expired: 'error',
  };

  const statusIcons = {
    valid: <ValidIcon />,
    expiring_soon: <ExpiringIcon />,
    expired: <ExpiredIcon />,
  };

  const entityIcons = {
    driver: <PersonIcon />,
    vehicle: <VehicleIcon />,
    company: <CompanyIcon />,
    voyage: <VoyageIcon />,
    invoice: <InvoiceIcon />,
    interlocuteur: <ContactIcon />,
    user: <UserIcon />,
    tarification: <TarificationIcon />,
  };

  // Function to handle creating multiple documents
  const handleCreateMultipleDocuments = () => {
    setEditingDocument(null);
    setFormData({
      document_type: '',
      document_name: '-- ',
      issue_date: null,
      expiry_date: null,
      related_entity_type: 'vehicle', // Default to vehicle
      related_entity_id: '',
      comments: '',
    });
    setEntityOptions([]);
    setMultipleDocuments([]); // Reset the list
    setOpenMultipleDialog(true); // Use the separate state
    
    // Fetch vehicle options immediately
    fetchEntityOptions('vehicle');
  };

  // Function to add a document to the list
  const handleAddDocumentToList = () => {
    // Validate required fields before adding
    if (!formData.document_type || !formData.related_entity_id || !formData.issue_date) {
      showSnackbar('Veuillez remplir tous les champs obligatoires', 'error');
      return;
    }

    // Add current form data to the list
    setMultipleDocuments([...multipleDocuments, { ...formData }]);

    // Reset form fields except for the ones that should stay the same
    setFormData({
      ...formData,
      document_type: '',
      issue_date: null,
      expiry_date: null,
      comments: '',
    });
  };

  // Function to save all documents
  const handleSaveMultipleDocuments = async () => {
    if (multipleDocuments.length === 0) {
      showSnackbar('Aucun document à ajouter', 'error');
      return;
    }

    try {
      // Save all documents in the multipleDocuments array
      for (const doc of multipleDocuments) {
        const payload = {
          ...doc,
          issue_date: doc.issue_date,
          expiry_date: doc.expiry_date,
        };
        
        await axios.post(`${API}/documents`, payload, {
          headers: { Authorization: `Bearer ${authState.token}` },
        });
        
        logOperation({
          operation_type: "CREATE",
          entity_type: "DOCUMENTS",
          entity_id: parsedAuthState.user.id,
          description: "Cree un document multiple pour véhicule ID : " + doc.related_entity_id,
        });
      }
      
      showSnackbar(`${multipleDocuments.length} document(s) créé(s) avec succès`, 'success');
      setOpenMultipleDialog(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error saving multiple documents:', error);
      showSnackbar('Erreur lors de la sauvegarde des documents', 'error');
    }
  };

  // Function to remove a document from the list
  const handleRemoveDocumentFromList = (index) => {
    const updatedList = [...multipleDocuments];
    updatedList.splice(index, 1);
    setMultipleDocuments(updatedList);
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      setDocuments(response.data.data.data);

      // Calculate KPIs
      const total = response.data.data.data.length;
      const valid = response.data.data.data.filter(doc => doc.status === 'valid').length;
      const expiring = response.data.data.data.filter(doc => doc.status === 'expiring_soon').length;
      const expired = response.data.data.data.filter(doc => doc.status === 'expired').length;

      setKpis({ total, valid, expiring, expired });
    } catch (error) {
      console.error('Error fetching documents:', error);
      showSnackbar('Erreur lors du chargement des documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await axios.get(`${API}/documents/form-options`, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      setFormOptions(response.data.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchFormOptions();
    console.log("AUth ", parsedAuthState)
  }, []);

  const fetchEntityOptions = async (entityType) => {
    try {
      setLoadingEntities(true);
      const response = await axios.get(`${API}/documents/entities-by-type`, {
        params: { entity_type: entityType },
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      setEntityOptions(response.data.data.entities);
    } catch (error) {
      console.error('Error fetching entity options:', error);
      showSnackbar('Erreur lors du chargement des données', 'error');
      setEntityOptions([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleCreateDocument = () => {
    setEditingDocument(null);
    setFormData({
      document_type: '',
      document_name: '',
      issue_date: null,
      expiry_date: null,
      related_entity_type: '',
      related_entity_id: '',
      comments: '',
    });
    setEntityOptions([]);
    setOpenDialog(true);
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setFormData({
      document_type: document.document_type,
      document_name: document.document_name,
      issue_date: document.issue_date ? new Date(document.issue_date) : null,
      expiry_date: document.expiry_date ? new Date(document.expiry_date) : null,
      related_entity_type: document.related_entity_type,
      related_entity_id: document.related_entity_id,
      comments: document.comments || '',
    });
    if (document.related_entity_type) {
      fetchEntityOptions(document.related_entity_type);
    }
    setOpenDialog(true);
  };

  const validateForm = () => {
    const errors = [];

    // Validation des champs obligatoires (plus stricte pour la création)
    if (!formData.document_type) {
      errors.push('Le type de document est obligatoire');
    }

    if (!formData.document_name || formData.document_name.trim() === '') {
      errors.push('Le nom du document est obligatoire');
    }

    if (!formData.issue_date) {
      errors.push('La date d\'émission est obligatoire');
    }

    // Validation conditionnelle pour les entités liées
    if (formData.related_entity_type && !formData.related_entity_id) {
      errors.push('Vous devez sélectionner un élément spécifique pour l\'entité liée');
    }

    return errors;
  };

  const handleSaveDocument = async () => {
    // Validation côté client
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      const errorMessage = 'Informations manquantes :\n• ' + validationErrors.join('\n• ');
      showSnackbar(errorMessage, 'error');
      return;
    }

    try {
      console.log(formData);
      const payload = {
        ...formData,
        issue_date: formData.issue_date,
        expiry_date: formData.expiry_date,
      };

      if (editingDocument) {
        await axios.put(`${API}/documents/${editingDocument.id}`, payload, {
          headers: { Authorization: `Bearer ${authState.token}` },
        });
        showSnackbar('Document modifié avec succès', 'success');
        logOperation({
          operation_type: "UPDATE",
          entity_type: "DOCUMENTS",
          entity_id: parsedAuthState.user.id,
          description: "Modifie un document  ID : " + editingDocument.id,
        });
      } else {
        await axios.post(`${API}/documents`, payload, {
          headers: { Authorization: `Bearer ${authState.token}` },
        });
        showSnackbar('Document créé avec succès', 'success');
        logOperation({
          operation_type: "CREATE",
          entity_type: "DOCUMENTS",
          entity_id: parsedAuthState.user.id,
          description: "Cree un document ",
        });
      }

      setOpenDialog(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);

      // Gestion des erreurs de validation du backend
      if (error.response && error.response.data && error.response.data.errors) {
        const backendErrors = error.response.data.errors;
        const errorMessages = [];

        Object.keys(backendErrors).forEach(field => {
          const fieldErrors = backendErrors[field];
          fieldErrors.forEach(err => {
            errorMessages.push(err);
          });
        });

        if (errorMessages.length > 0) {
          showSnackbar('Erreurs de validation :\n• ' + errorMessages.join('\n• '), 'error');
          return;
        }
      }

      // Gestion des autres erreurs
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await axios.delete(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${authState.token}` },
      });
      showSnackbar('Document supprimé avec succès', 'success');
      logOperation({
        operation_type: "DELETE",
        entity_type: "DOCUMENTS",
        entity_id: parsedAuthState.user.id,
        description: "Supprime un document  ID : " + documentId,
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      showSnackbar('Erreur lors de la suppression', 'error');
    }
  };

  const handleEntityTypeChange = (entityType) => {
    setFormData({ ...formData, related_entity_type: entityType, related_entity_id: '' });
    if (entityType) {
      fetchEntityOptions(entityType);
    } else {
      setEntityOptions([]);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMenuOpen = (event, document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocument(null);
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.comments?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || doc.document_type === filterType;
    const matchesStatus = !filterStatus || doc.status === filterStatus;
    const matchesEntity = !filterEntity || doc.related_entity_type === filterEntity;

    return matchesSearch && matchesType && matchesStatus && matchesEntity;
  });

  const getStatusChip = (status) => {
    const statusLabels = {
      valid: 'Valide',
      expiring_soon: 'Expire bientôt',
      expired: 'Expiré',
    };

    return (
      <Chip
        icon={statusIcons[status]}
        label={statusLabels[status]}
        color={statusColors[status]}
        size="small"
        variant="outlined"
      />
    );
  };

  const getEntityDisplayName = (document) => {
    if (!document.related_entity_type || !document.related_entity_id) {
      return 'Non lié';
    }

    // This would need to be enhanced with actual entity data
    return `${entityTypes[document.related_entity_type]} #${document.related_entity_id}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Box sx={{
        backgroundColor: '#f8fafc',
        padding: { xs: '16px', md: '20px' },
        minHeight: '116vh',
        transition: 'all 0.3s ease-in-out'
      }}>
        {/* Header */}
        <Box sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb4b',
          borderRadius: '32px',
          padding: { xs: '20px', md: '30px' },
          marginBottom: '20px',
          boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid #f3f4f6'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Box sx={{
                width: '40px',
                height: '40px',
                borderRadius: '6px',
                backgroundColor: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <DocumentIcon style={{ color: 'white', fontSize: '20px' }} />
              </Box>
              <Typography variant="h4" sx={{
                fontWeight: 600,
                color: '#1f2937',
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                letterSpacing: '-0.025em'
              }}>
                Gestion des Documents
              </Typography>
            </Box>

            <Typography variant="body2" sx={{
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: 500,
              backgroundColor: '#f9fafb',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              {documents.length} document{documents.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{
            color: '#6b7280',
            fontSize: '14px',
            fontWeight: 500
          }}>
            Gérez tous vos documents avec des rappels intelligents et des alertes automatiques
          </Typography>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb4b',
              borderRadius: '32px',
              padding: { xs: '20px', md: '30px' },
              marginBottom: '20px',
              boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
            }} >
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <DocumentIcon />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Documents
                    </Typography>
                    <Typography variant="h4">
                      {kpis.total}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb4b',
              borderRadius: '32px',
              padding: { xs: '20px', md: '30px' },
              marginBottom: '20px',
              boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
            }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <ValidIcon />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Valides
                    </Typography>
                    <Typography variant="h4">
                      {kpis.valid}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb4b',
              borderRadius: '32px',
              padding: { xs: '20px', md: '30px' },
              marginBottom: '20px',
              boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
            }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <ExpiringIcon />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Expirent bientôt
                    </Typography>
                    <Typography variant="h4">
                      {kpis.expiring}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb4b',
              borderRadius: '32px',
              padding: { xs: '20px', md: '30px' },
              marginBottom: '20px',
              boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
            }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                    <ExpiredIcon />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Expirés
                    </Typography>
                    <Typography variant="h4">
                      {kpis.expired}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Actions */}
        <Card sx={{
          mb: 3,
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb4b',
          borderRadius: '32px',
          padding: { xs: '20px', md: '30px' },
          marginBottom: '20px',
          boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
        }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">

              {/* 🔍 Search */}
              <Grid item xs={12} md={4}>
                <TextInputMui
                  type="text"
                  label="Rechercher des documents"
                  icon={<SearchIcon />}
                  value={searchTerm}
                  setValue={setSearchTerm}
                />
              </Grid>

              {/* 🔵 Type */}
              <Grid item xs={12} md={2}>
                <AutocompleteComboBox
                  list={[
                    { value: "", label: "Tous" },
                    ...Object.entries(documentTypes).map(([key, label]) => ({
                      value: key,
                      label,
                    })),
                  ]}
                  Label="Type"
                  keyField="value"
                  valueField="label"
                  value={filterType}
                  onChange={(selected) => {
                    console.log(selected);
                    setFilterType(selected);
                  }}
                  widthSpecific="100%"
                />
              </Grid>

              {/* 🟣 Statut */}
              <Grid item xs={12} md={2}>
                <AutocompleteComboBox
                  list={[
                    { value: "Tous", label: "Tous" },
                    { value: "valid", label: "Valide" },
                    { value: "expiring_soon", label: "Expire bientôt" },
                    { value: "expired", label: "Expiré" },
                  ]}
                  Label="Statut"
                  keyField="value"
                  valueField="label"
                  value={filterStatus}

                  onChange={(value) => {
                    setFilterStatus(value);
                  }}
                  widthSpecific="100%"
                />
              </Grid>

              {/* 🟠 Entité */}
              <Grid item xs={12} md={2}>
                <AutocompleteComboBox
                  list={[
                    { value: "", label: "Toutes" },
                    ...Object.entries(entityTypes).map(([key, label]) => ({
                      value: key,
                      label,
                    })),
                  ]}
                  Label="Entité"
                  keyField="value"
                  valueField="label"
                  value={filterEntity}
                  onChange={(selected) => {
                    setFilterEntity(selected);
                  }}
                  widthSpecific="100%"
                />
              </Grid>

              {/* ➕ Nouveau */}
              <Grid item xs={12} md={2}>
                <ButtonAttribute
                  type="button"
                  Bgcolor="#1976d2"
                  color="#fff"
                  bgColorHover="#115293"
                  ColorHover="#fff"
                  startIcon={<AddIcon />}
                  Label="Nouveau"
                  action={handleCreateDocument}
                  width="100%"
                  height="42px"
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <ButtonAttribute
                  type="button"
                  Bgcolor="#1976d2"
                  color="#fff"
                  bgColorHover="#115293"
                  ColorHover="#fff"
                  startIcon={<AddIcon />}
                  Label="Ajouter multiples documents "
                  action={handleCreateMultipleDocuments}
                  width="300px"
                  height="42px"
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>

        </Card>

        {/* Documents Table */}
        <Card sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb4b',
          borderRadius: '32px',
          padding: { xs: '20px', md: '30px' },
          marginBottom: '20px',
          boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
        }} >
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Document</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Entité liée</TableCell>
                    <TableCell>Dates</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocuments.map((document) => (
                    <TableRow key={document.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {document.document_name}
                          </Typography>
                          {document.comments && (
                            <Typography variant="body2" color="text.secondary">
                              {document.comments}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={documentTypes[document.document_type]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {entityIcons[document.related_entity_type]}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {getEntityDisplayName(document)}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            Émis: {document.issue_date ? new Date(document.issue_date).toLocaleDateString('fr-FR') : 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            Expire: {document.expiry_date ? new Date(document.expiry_date).toLocaleDateString('fr-FR') : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        {getStatusChip(document.status)}
                      </TableCell>

                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, document)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            handleEditDocument(selectedDocument);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} />
            Modifier
          </MenuItem>
          <MenuItem onClick={() => {
            handleDeleteDocument(selectedDocument.id);
            handleMenuClose();
          }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Supprimer
          </MenuItem>
        </Menu>

        {/* Document Form Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md"
          sx={{

            border: '1px solid #e5e7eb4b',

            padding: { xs: '20px', md: '30px' },
            marginBottom: '20px',
            boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
          }} >
          <DialogTitle>
            {editingDocument ? 'Modifier le document' : 'Nouveau document'}
          </DialogTitle>

          <DialogContent>

            {/* Info banner */}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note :</strong> Les champs marqués d'un astérisque (*) sont obligatoires.
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mt: 1 }}>

              {/* 🔵 Type de document */}
              <Grid item xs={12} md={6}>
                <AutocompleteComboBox
                  list={Object.entries(documentTypes).map(([key, label]) => ({
                    value: key,
                    label
                  }))}
                  Label="Type de document "
                  keyField="value"
                  valueField="label"
                  required
                  value={formData.document_type}
                  onChange={(selected) => {
                    setFormData({ ...formData, document_type: selected });
                  }}
                  widthSpecific="100%"
                  sx={{
                    '& .MuiInputBase-root': {
                      borderColor: !formData.document_type ? 'red' : undefined
                    }
                  }}
                />
              </Grid>

              {/* 📝 Nom du document */}
              <Grid item xs={12} md={6}>
                <TextInputMui
                  label="Nom du document"
                  required
                  type={'text'}
                  value={formData.document_name}
                  setValue={(val) => setFormData({ ...formData, document_name: val })}
                />

              </Grid>

              {/* 📅 Date d'émission */}
              <Grid item xs={12} md={6}>
                <DateTimePickerComponent
                  label="Valable du "
                  typeDate="date"
                  required
                  value={editingDocument ? dayjs(formData.issue_date).format('YYYY-MM-DD') : null}
                  dateSelected={(val) => setFormData({ ...formData, issue_date: val })}
                />
                {!formData.issue_date == null && (
                  <Typography variant="caption" color="error">
                    La date d'émission est obligatoire
                  </Typography>
                )}
              </Grid>

              {/* 📅 Date d'expiration */}
              <Grid item xs={12} md={6} >
                <DateTimePickerComponent
                  label="Valable du"
                  typeDate="date"
                  value={editingDocument ? dayjs(formData.expiry_date).format('YYYY-MM-DD') : null}
                  dateSelected={(val) => setFormData({ ...formData, expiry_date: val })}
                />
              </Grid>

              {/* 🧩 Entité liée */}
              <Grid item xs={12} md={6}>
                <AutocompleteComboBox
                  list={Object.entries(entityTypes).map(([key, label]) => ({
                    value: key,
                    label
                  }))}
                  Label="Entité liée"
                  keyField="value"
                  valueField="label"
                  value={formData.related_entity_type}
                  onChange={(selected) => {

                    handleEntityTypeChange(selected);
                  }}
                  widthSpecific="100%"
                />
              </Grid>

              {/* 🧱 Élément spécifique */}
              <Grid item xs={12} md={6}>
                <AutocompleteComboBox
                  list={
                    loadingEntities
                      ? []
                      : entityOptions.map((entity) => ({
                        value: entity.id,
                        label: `${entity.name} - ${entity.details}`
                      }))
                  }
                  Label={`Élément spécifique${formData.related_entity_type ? ' *' : ''}`}
                  keyField="value"
                  valueField="label"
                  value={formData.related_entity_id}
                  onChange={(selected) => {

                    setFormData({ ...formData, related_entity_id: selected });
                  }}
                  disable={!formData.related_entity_type || loadingEntities}
                  widthSpecific="100%"
                />

                {loadingEntities && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Chargement...</Typography>
                  </Box>
                )}

                {formData.related_entity_type && !formData.related_entity_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Vous devez sélectionner un élément spécifique
                  </Typography>
                )}
              </Grid>

              {/* 🗒️ Commentaires */}
              <Grid item xs={12}>
                <TextInputMui
                  label="Commentaires"
                  multiline
                  rows={3}
                  value={formData.comments}
                  setValue={(val) => setFormData({ ...formData, comments: val })}
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions>

            <ButtonAttribute
              type="button"
              Bgcolor="transparent"
              color={"#1976d2"}
              bgColorHover="rgba(25,118,210,0.08)"
              ColorHover={"#fff"}
              Label="Annuler"
              action={() => setOpenDialog(false)}
              width="auto"
              height="40px"
              sx={{ px: 2, borderRadius: "8px" }}
            />

            <ButtonAttribute
              type="button"
              Bgcolor="#1976d2"
              color="#fff"
              bgColorHover="#115293"
              ColorHover="#fff"
              Label={editingDocument ? "Modifier" : "Créer"}
              startIcon={null}
              action={handleSaveDocument}
              width="auto"
              height="40px"
              sx={{
                px: 3,
                borderRadius: "8px",
                fontWeight: 600,
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
              }}
            />

          </DialogActions>

        </Dialog>

        {/* Multiple documents Dialog */}
        <Dialog
          open={openMultipleDialog}
          onClose={() => setOpenMultipleDialog(false)}
          maxWidth="lg"  // Increased width to accommodate both cards
          fullWidth
          sx={{
            border: '1px solid #e5e7eb4b',
            padding: { xs: '20px', md: '30px' },
            marginBottom: '20px',
            boxShadow: "0 4px 6px rgba(99, 102, 241, 0.2)",
          }}
        >
          <DialogTitle>
            Ajouter multiples documents pour un véhicule
          </DialogTitle>

          <DialogContent>
            {/* Info banner */}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note :</strong> Les champs marqués d'un astérisque (*) sont obligatoires.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Card 1: Vehicle Information */}
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Informations du véhicule
                  </Typography>

                  {/* Entité liée */}
                  <Box sx={{ mt :2}} >
                  <AutocompleteComboBox
                    list={Object.entries(entityTypes).map(([key, label]) => ({
                      value: key,
                      label
                    }))}
                    Label="Entité liée"
                    keyField="value"
                    valueField="label"
                    value={formData.related_entity_type}
                    onChange={(selected) => {
                      handleEntityTypeChange(selected);
                    }}
                    widthSpecific="100%"
                    disabled={true} // Always set to "Véhicule"
                  />
                  </Box>

                  {/* Nom du document (disabled) */}
                  <Box sx={{ mt :2}}>
                  <TextInputMui
                    label="Nom du document"
                    type={'text'}
                    value={formData.document_name}
                    setValue={(val) => setFormData({ ...formData, document_name: val })}
                    disabled={true} // Always 'IMMOBILISATION'
                    
                  />
                  </Box>

                  {/* Élément spécifique (Vehicle selection) */}
                  <Box sx={{ mt :2}}>
                  <AutocompleteComboBox
                    list={
                      loadingEntities
                        ? []
                        : entityOptions.map((entity) => ({
                          value: entity.id,
                          label: `${entity.name} - ${entity.details}`
                        }))
                    }
                    Label={`Élément spécifique *`}
                    keyField="value"
                    valueField="label"
                    value={formData.related_entity_id}
                    onChange={(selected) => {
                      setFormData({ ...formData, related_entity_id: selected });
                    }}
                    disable={!formData.related_entity_type || loadingEntities}
                    widthSpecific="100%"
                    sx={{ mt: 10 }}
                  /></Box>

                  {loadingEntities && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption">Chargement...</Typography>
                    </Box>
                  )}

                  {formData.related_entity_type && !formData.related_entity_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      Vous devez sélectionner un élément spécifique
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Card 2: Document Information */}
              <Grid item xs={12} md={8}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Informations du document
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Type de document */}
                    <Grid item xs={12} md={12}>
                      <AutocompleteComboBox
                        list={Object.entries(documentTypes).map(([key, label]) => ({
                          value: key,
                          label
                        }))}
                        Label="Type de document *"
                        keyField="value"
                        valueField="label"
                        required
                        value={formData.document_type}
                        onChange={(selected) => {
                          setFormData({ ...formData, document_type: selected });
                        }}
                        widthSpecific="100%"
                        sx={{
                          '& .MuiInputBase-root': {
                            borderColor: !formData.document_type ? 'red' : undefined
                          }
                        }}
                      />
                    </Grid>

                    {/* Date d'émission */}
                    <Grid item xs={12} md={6}>
                      <DateTimePickerComponent
                        label="Valable du *"
                        typeDate="date"
                        required
                        value={formData.issue_date ? dayjs(formData.issue_date).format('YYYY-MM-DD') : null}
                        dateSelected={(val) => setFormData({ ...formData, issue_date: val })}
                      />
                      {!formData.issue_date && (
                        <Typography variant="caption" color="error">
                          La date d'émission est obligatoire
                        </Typography>
                      )}
                    </Grid>

                    {/* Date d'expiration */}
                    <Grid item xs={12} md={6}>
                      <DateTimePickerComponent
                        label="Valable jusqu'au"
                        typeDate="date"
                        value={formData.expiry_date ? dayjs(formData.expiry_date).format('YYYY-MM-DD') : null}
                        dateSelected={(val) => setFormData({ ...formData, expiry_date: val })}
                      />
                    </Grid>

                    {/* Commentaires */}
                    <Grid item xs={12}>
                      <TextInputMui
                        label="Commentaires"
                        multiline
                        rows={3}
                        value={formData.comments}
                        setValue={(val) => setFormData({ ...formData, comments: val })}
                      />
                    </Grid>

                    {/* Add button */}
                    <Grid item xs={12}>
                      <ButtonAttribute
                        type="button"
                        Bgcolor="#1976d2"
                        color="#fff"
                        bgColorHover="#115293"
                        ColorHover="#fff"
                        startIcon={<AddIcon />}
                        Label="Ajouter à la liste"
                        action={handleAddDocumentToList}
                        width="100%"
                        height="40px"
                        sx={{
                          borderRadius: "8px",
                          fontWeight: 600,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>

            {/* List of added documents */}
            {multipleDocuments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Documents à ajouter ({multipleDocuments.length})
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List>
                    {multipleDocuments.map((doc, index) => (
                      <ListItem key={index} divider>
                        <ListItemText
                          primary={`${documentTypes[doc.document_type] || doc.document_type} - ${dayjs(doc.issue_date).format('DD/MM/YYYY')}`}
                          secondary={doc.comments || 'Aucun commentaire'}
                        />
                        <IconButton edge="end" onClick={() => handleRemoveDocumentFromList(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <ButtonAttribute
              type="button"
              Bgcolor="transparent"
              color={"#1976d2"}
              bgColorHover="rgba(25,118,210,0.08)"
              ColorHover={"#fff"}
              Label="Annuler"
              action={() => setOpenMultipleDialog(false)}
              width="auto"
              height="40px"
              sx={{ px: 2, borderRadius: "8px" }}
            />

            <ButtonAttribute
              type="button"
              Bgcolor="#1976d2"
              color="#fff"
              bgColorHover="#115293"
              ColorHover="#fff"
              Label={`Enregistrer ${multipleDocuments.length} document(s)`}
              startIcon={null}
              action={handleSaveMultipleDocuments}
              width="auto"
              height="40px"
              disabled={multipleDocuments.length === 0}
              sx={{
                px: 3,
                borderRadius: "8px",
                fontWeight: 600,
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
              }}
            />
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default DocumentManagement;